import { CommonModule } from "@angular/common";
import { Component, OnInit, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { finalize, timeout } from "rxjs";
import swal from "sweetalert2";

import {
  DatosFormularioIso,
  FormularioIncidenciaIsoBase,
  ModoFormularioIso,
  TextosFormularioIso,
} from "../../shared/incidencias-iso/incidencia-iso-form-base";
import {
  idUsuarioEmpleadoIso,
  mensajeErrorHttpIso,
  nombreEmpleadoIso,
  perfilEmpleadoIso,
  puestoEmpleadoIso,
} from "../../shared/incidencias-iso/incidencias-iso.comun";
import { Empleado } from "../../shared/model/empleado/empleado.model";
import {
  IncidenciaSeguridad,
  IncidenciaSeguridadPorques,
} from "../../shared/model/iso42001/incidencia-seguridad.model";
import {
  GRAVEDAD_OPTIONS,
  PRIORIDAD_OPTIONS,
  TIPO_EVENTO_OPTIONS,
  TIPO_SOLUCION_OPTIONS,
} from "../incidencias-seguridad.constants";
import { IncidenciasSeguridadService } from "../incidencias-seguridad.service";

export type IncidenciaSeguridadFormMode = ModoFormularioIso;

export type IncidenciaSeguridadFormData = DatosFormularioIso<IncidenciaSeguridad>;

@Component({
  selector: "app-incidencia-seguridad-form",
  standalone: true,
  templateUrl: "./incidencia-seguridad-form.component.html",
  styleUrls: ["./incidencia-seguridad-form.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTooltipModule,
  ],
})
export class IncidenciaSeguridadFormComponent extends FormularioIncidenciaIsoBase<IncidenciaSeguridad> implements OnInit {
  readonly tipoEventoOptions = TIPO_EVENTO_OPTIONS;
  readonly gravedadOptions = GRAVEDAD_OPTIONS;
  readonly prioridadOptions = PRIORIDAD_OPTIONS;
  readonly tipoSolucionOptions = TIPO_SOLUCION_OPTIONS;

  incidencia: IncidenciaSeguridad = this.crearVacia();
  responsables: Empleado[] = [];
  mensajeExito = "";

  protected readonly incidenciasService = inject(IncidenciasSeguridadService);
  protected readonly textos: TextosFormularioIso = {
    guardando: "Guardando...",
    faltanCincoPorques: "Captura al menos los primeros 5 porqués.",
  };

  ngOnInit(): void {
    this.data = this.data || { mode: "alta" };
    this.cargarResponsables();
    if (this.data.incidencia) {
      this.incidencia = this.normalizarFechas({ ...this.data.incidencia });
      this.prepararSeguimiento();
      this.cargarPorquesSeguros();
      if (this.incidencia.idIncidenciaIa && this.debeCargarDetalleInicial()) {
        this.cargarDatosComplementarios(this.incidencia.idIncidenciaIa);
      }
      return;
    }
    if (this.data.idIncidenciaIa) {
      this.cargarIncidenciaSegura(this.data.idIncidenciaIa);
      return;
    }
    this.prepararSeguimiento();
  }

  get soloLecturaCampos(): boolean {
    return this.incidencia.estadoEvento === "ATENDIDO"
      || this.incidencia.estadoEvento === "VOBO"
      || this.incidencia.estadoEvento === "CANCELADO";
  }

  get soloConsulta(): boolean {
    return this.incidencia.estadoEvento === "VOBO" || this.incidencia.estadoEvento === "CANCELADO";
  }

  get titulo(): string {
    if (this.incidencia.estadoEvento === "ATENDIDO") {
      return "Revisión de incidencia de seguridad";
    }
    if (this.incidencia.estadoEvento === "VOBO") {
      return "Consulta de incidencia de seguridad";
    }
    if (this.incidencia.estadoEvento === "CANCELADO") {
      return "Consulta de incidencia de seguridad cancelada";
    }
    if (this.muestraSeguimiento) {
      return "Proceso de incidencia de seguridad";
    }
    return this.esEdicion ? "Editar incidencia de seguridad" : "Nueva incidencia de seguridad";
  }

  guardar(): void {
    this.error = "";
    this.advertencia = "";
    this.mensajeExito = "";
    if (!this.validar()) {
      return;
    }
    this.prepararResponsableSeguimiento();
    this.prepararCausaOrigenParaGuardar();
    this.guardando = true;

    this.solicitudGuardar().subscribe({
      next: (res) => {
        if (this.guardadoConError(res, "No fue posible guardar la incidencia de seguridad.")) {
          return;
        }
        const idIncidenciaIa = this.incidencia.idIncidenciaIa || res.idGenerado;
        this.advertencia = res.advertencia || "";
        this.guardando = false;
        if (this.esEdicion || this.esSeguimiento) {
          this.incidencia.idIncidenciaIa = idIncidenciaIa;
          this.mensajeExito = this.advertencia || "Cambios guardados correctamente.";
          this.nuevoComentario = "";
          if (idIncidenciaIa) {
            this.cargarDatosComplementarios(idIncidenciaIa);
          }
          return;
        }
        this.avisarGuardadoYCerrar(
          "Incidencia de seguridad guardada",
          `Se generó la incidencia con folio: ${idIncidenciaIa || "-"}.`,
          idIncidenciaIa,
        );
      },
      error: (e) => {
        this.mostrarErrorGuardar(e?.error?.mensajeError || e?.error?.message || "No fue posible guardar la incidencia de seguridad.");
        this.guardando = false;
      },
    });
  }

  onResponsableSeguimientoChange(): void {
    this.prepararResponsableSeguimiento();
  }

  preguntaPorque(index: number): string {
    if (index === 0) {
      return `¿Por qué se presentó ${this.labelTipo(this.incidencia.tipoIncidencia) || "el evento"}?`;
    }
    const anterior = this.quitarPrefijoPorque(this.porques[index - 1]) || `la respuesta ${index}`;
    return `¿Por qué ${anterior}?`;
  }

  getDescripcionTipoSolucion(): string {
    return this.tipoSolucionOptions.find((option) => option.value === this.incidencia.tipoSolucion)?.description || "";
  }

  abrirInformacionCausaOrigen(): void {
    swal.fire({
      title: "Causa / Origen",
      html: `
        <div style="text-align:left;">
          <p><strong>1. ¿Por qué el sistema está arrojando datos desactualizados?</strong><br>Porque el analista no cargó los puntos del mes anterior.</p>
          <p><strong>2. ¿Por qué el analista no cargó los puntos del mes anterior?</strong><br>Porque nadie le enseñó cómo hacerlo.</p>
          <p><strong>3. ¿Por qué nadie le enseñó cómo hacerlo?</strong><br>Porque nadie conoce el procedimiento y la persona que sí lo conocía renunció.</p>
          <p><strong>4. ¿Por qué nadie conoció el procedimiento?</strong><br>Porque la anterior persona no se lo explicó a nadie y nunca se documentó.</p>
          <p><strong>5. ¿Por qué no se explicó a nadie ni se documentó?</strong><br>Porque no se ha estimado la probabilidad de que una persona crítica no pueda seguir en la empresa, su dificultad de reemplazo y la complejidad del procedimiento.</p>
          <p style="margin-top:16px;">
            Para más información ir al sitio
            <a href="http://10.0.1.226/images/stories/Documentos-Nuevos/nuevos/ORG/INO-005_5porques.pdf" target="_blank" rel="noopener noreferrer">
              5 porqués
            </a>
          </p>
        </div>
      `,
      icon: "info",
      confirmButtonText: "Cerrar",
    });
  }

  private cargarResponsables(): void {
    this.incidenciasService.consultarResponsables().pipe(timeout(15000)).subscribe({
      next: (res) => {
        try {
          this.responsables = res?.responsables || [];
          this.prepararResponsableSeguimientoPorDefecto();
          this.prepararResponsableSeguimiento();
        } catch {
          this.responsables = [];
        }
      },
      error: (e) => {
        this.responsables = [];
      },
    });
  }

  private cargarIncidencia(id: number): void {
    this.cargando = true;
    this.incidenciasService.obtener(id).pipe(
      timeout(15000),
      finalize(() => {
        this.cargando = false;
      }),
    ).subscribe({
      next: (res) => {
        if (!res?.incidencia) {
          this.error = "No se encontró la incidencia de seguridad.";
          this.cargando = false;
          return;
        }
        this.incidencia = this.normalizarFechas(res.incidencia);
        this.comentarios = res.comentarios || [];
        this.prepararSeguimiento();
        this.cargarPorques();
        this.prepararResponsableSeguimientoPorDefecto();
        this.prepararResponsableSeguimiento();
        this.cargando = false;
      },
      error: () => {
        this.error = "No fue posible cargar la incidencia de seguridad.";
        this.cargando = false;
      },
    });
  }

  private cargarIncidenciaSegura(id: number): void {
    this.cargando = true;
    this.incidenciasService.obtener(id).pipe(
      timeout(15000),
      finalize(() => {
        this.cargando = false;
      }),
    ).subscribe({
      next: (res) => {
        try {
          if (!res?.incidencia) {
            this.error = "No se encontro la incidencia de seguridad.";
            return;
          }
          this.incidencia = this.normalizarFechas(res.incidencia);
          this.comentarios = res.comentarios || [];
          this.prepararSeguimiento();
          this.cargarPorquesSeguros();
          this.prepararResponsableSeguimientoPorDefecto();
          this.prepararResponsableSeguimiento();
        } catch {
          this.error = "No fue posible interpretar la incidencia de seguridad.";
        }
      },
      error: (e) => {
        this.error = this.mensajeErrorHttp(e, "No fue posible cargar la incidencia de seguridad.");
      },
    });
  }

  private cargarDatosComplementarios(id: number): void {
    this.incidenciasService.obtener(id).pipe(timeout(15000)).subscribe({
      next: (res) => {
        try {
          if (res?.incidencia) {
            this.incidencia = this.normalizarFechas(res.incidencia);
            this.prepararSeguimiento();
            this.cargarPorquesSeguros();
            this.prepararResponsableSeguimientoPorDefecto();
            this.prepararResponsableSeguimiento();
          }
          this.comentarios = res?.comentarios || [];
        } catch {
          this.advertencia = "No fue posible completar la carga del seguimiento con todos los datos.";
        }
      },
      error: () => {
        this.comentarios = this.comentarios || [];
      },
    });
  }

  private debeCargarDetalleInicial(): boolean {
    return !this.esEdicion || this.incidencia.estadoEvento !== "ABIERTO";
  }

  private validar(): boolean {
    if (this.muestraSeguimiento) {
      if (this.soloConsulta) {
        this.error = "La incidencia en Vo.Bo. solo puede consultarse.";
        return false;
      }
      if (this.soloLecturaCampos) {
        if (!this.nuevoComentario.trim()) {
          this.error = "Agrega un comentario antes de guardar.";
          return false;
        }
        return true;
      }
      if (!this.validarPorquesSinSaltos()) {
        return false;
      }
      if (!this.validarCincoPorques()) {
        return false;
      }
      if (!this.incidencia.fechaPlaneadaResolucion) {
        this.error = "La fecha planeada de resolución es obligatoria.";
        return false;
      }
      if (!this.incidencia.accionCorrectiva?.trim()) {
        this.error = "La acción correctiva es obligatoria.";
        return false;
      }
      if (!this.incidencia.tipoSolucion) {
        this.error = "Selecciona el tipo de solución.";
        return false;
      }
      if (!this.incidencia.idEmpleadoResponsableSeguimiento) {
        this.error = "Selecciona el responsable de seguimiento.";
        return false;
      }
      return true;
    }
    if (!this.incidencia.procesoProcedimientoAfectado?.trim()) {
      this.error = "Indica el proceso o procedimiento afectado.";
      return false;
    }
    if (!this.incidencia.tipoIncidencia) {
      this.error = "Selecciona el tipo.";
      return false;
    }
    if (!this.incidencia.descripcionEvento?.trim()) {
      this.error = "Describe la incidencia.";
      return false;
    }
    if (!this.incidencia.gravedad) {
      this.error = "Selecciona la gravedad.";
      return false;
    }
    if (!this.incidencia.prioridad) {
      this.error = "Selecciona la prioridad.";
      return false;
    }
    return true;
  }

  private cargarPorquesSeguros(): void {
    const porques = this.parsePorquesSeguros(this.incidencia.causaOrigen);
    const total = Math.max(5, ...Object.keys(porques).map((key) => Number(key) || 0));
    this.porques = Array.from({ length: total }, (_item, index) => porques[String(index + 1)] || "");
    const causaOrigenTexto = this.normalizarTexto(this.incidencia.causaOrigen);
    if (!Object.keys(porques).length && causaOrigenTexto) {
      this.porques[0] = causaOrigenTexto;
    }
  }

  private parsePorquesSeguros(value?: string | Record<string, unknown>): IncidenciaSeguridadPorques {
    if (!value) {
      return {};
    }
    if (typeof value !== "string") {
      if (Array.isArray(value) || typeof value !== "object") {
        return {};
      }
      return Object.keys(value).reduce<IncidenciaSeguridadPorques>((acc, key) => {
        const index = Number(key);
        if (Number.isInteger(index) && index >= 1) {
          acc[String(index)] = this.normalizarTexto((value as Record<string, unknown>)[key]);
        }
        return acc;
      }, {});
    }
    if (!value.trim()) {
      return {};
    }
    try {
      const parsed = JSON.parse(value);
      if (!parsed || Array.isArray(parsed) || typeof parsed !== "object") {
        return {};
      }
      return Object.keys(parsed).reduce<IncidenciaSeguridadPorques>((acc, key) => {
        const index = Number(key);
        if (Number.isInteger(index) && index >= 1) {
          acc[String(index)] = this.normalizarTexto((parsed as Record<string, unknown>)[key]);
        }
        return acc;
      }, {});
    } catch {
      return {};
    }
  }

  protected normalizarPorque(value?: string): string {
    const limpio = value?.trim() || "";
    if (!limpio) {
      return "";
    }
    if (/^(por\s+que|por\s+que|porque)\b/i.test(limpio)) {
      return limpio;
    }
    return `Porque ${limpio}`;
  }

  private quitarPrefijoPorque(value?: string): string {
    return (value || "").trim().replace(/^(por\s+que|porque)\s+/i, "").trim();
  }

  private normalizarTexto(value: unknown): string {
    if (typeof value === "string") {
      return value.trim();
    }
    if (value === null || value === undefined) {
      return "";
    }
    return String(value).trim();
  }

  private labelTipo(value?: string): string {
    return this.tipoEventoOptions.find((option) => option.value === value)?.label || value || "";
  }

  private prepararResponsableSeguimientoPorDefecto(): void {
    if (!this.muestraSeguimiento || this.incidencia.idEmpleadoResponsableSeguimiento || !this.responsables.length) {
      return;
    }
    const responsable = this.responsables.find((item) => idUsuarioEmpleadoIso(item) === this.incidencia.idUsuarioAlta);
    if (!responsable) {
      return;
    }
    this.incidencia.idEmpleadoResponsableSeguimiento = responsable.idEmpleado;
  }

  private prepararResponsableSeguimiento(): void {
    if (!this.muestraSeguimiento || !this.incidencia.idEmpleadoResponsableSeguimiento) {
      return;
    }
    const responsable = this.responsables.find(
      (item) => item.idEmpleado === this.incidencia.idEmpleadoResponsableSeguimiento,
    );
    if (!responsable) {
      return;
    }
    this.incidencia.idUsuarioResponsableSeguimiento = idUsuarioEmpleadoIso(responsable);
    this.incidencia.nombreResponsableSeguimiento = nombreEmpleadoIso(responsable);
    this.incidencia.responsableSeguimientoCargo = puestoEmpleadoIso(responsable);
    this.incidencia.responsableSeguimientoPerfil = perfilEmpleadoIso(responsable);
  }

  private crearVacia(): IncidenciaSeguridad {
    return {
      estadoEvento: "ABIERTO",
      procesoProcedimientoAfectado: "",
      descripcionEvento: "",
      tipoIncidencia: "",
      gravedad: "",
      prioridad: "",
      causaOrigen: "",
      accionCorrectiva: "",
      tipoSolucion: "",
      observacionesAdicionales: "",
    };
  }

  private mensajeErrorHttp(e: any, fallback: string): string {
    if (e?.name === "TimeoutError") {
      return "La carga de la incidencia de seguridad tardo demasiado. Intenta de nuevo.";
    }
    return mensajeErrorHttpIso(e, fallback, {
      servicioNoEncontrado: "El backend no encontro el servicio de incidencias de seguridad. Revisa que CosRest este compilado y reiniciado.",
      respuestaNoJson: "El backend respondio una pagina en lugar de JSON. La ruta de incidencias de seguridad no esta llegando al controller.",
    });
  }
}
